import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, MapPin, RotateCcw, Search, X } from 'lucide-react';
import iranData from '../../data/iranCounties.json';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const FULL_VIEWBOX = [0, -25, 1200, 1120];

function parseViewBox(value) {
  const parts = String(value || '').trim().split(/\s+/).map(Number);
  return parts.length === 4 && parts.every(Number.isFinite) ? parts : null;
}

function normalizeValue(value) {
  return {
    province_code: String(value?.province_code || ''),
    province_name: String(value?.province_name || ''),
    county_id: String(value?.county_id || ''),
    county_name: String(value?.county_name || ''),
    address_line: String(value?.address_line || ''),
  };
}

export default function IranAddressPicker({ open, value, onClose, onConfirm }) {
  useBodyScrollLock(open);
  const mapPanelRef = useRef(null);
  const mapContainerRef = useRef(null);
  const currentViewBoxRef = useRef(FULL_VIEWBOX);
  const animationRef = useRef(null);
  const [svgText, setSvgText] = useState('');
  const [mapError, setMapError] = useState(false);
  const [draft, setDraft] = useState(() => normalizeValue(value));
  const [search, setSearch] = useState('');
  const [countyListOpen, setCountyListOpen] = useState(true);

  const counties = iranData.counties;
  const provinces = useMemo(() => {
    const map = new Map();
    counties.forEach((county) => {
      if (!map.has(county.provinceCode)) map.set(county.provinceCode, county.province);
    });
    return Array.from(map, ([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name, 'fa'));
  }, [counties]);

  const visibleCounties = useMemo(() => {
    const query = search.trim();
    return counties.filter((county) => (
      county.provinceCode === draft.province_code
      && (!query || county.name.includes(query))
    ));
  }, [counties, draft.province_code, search]);

  useEffect(() => {
    if (!open) return;
    setDraft(normalizeValue(value));
    setSearch('');
    setCountyListOpen(true);
  }, [open, value]);

  useEffect(() => {
    if (!open || svgText) return;
    fetch('/maps/iran-counties.svg')
      .then((response) => {
        if (!response.ok) throw new Error('Map file could not be loaded');
        return response.text();
      })
      .then((text) => {
        setSvgText(text);
        setMapError(false);
      })
      .catch(() => setMapError(true));
  }, [open, svgText]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      mapPanelRef.current?.classList.remove('is-zooming');
    };
  }, [open, onClose]);

  function selectProvince(code, name) {
    setDraft((current) => ({
      ...current,
      province_code: code,
      province_name: name,
      county_id: '',
      county_name: '',
    }));
    setSearch('');
    setCountyListOpen(true);
  }

  function selectCounty(county) {
    setDraft((current) => ({
      ...current,
      province_code: county.provinceCode,
      province_name: county.province,
      county_id: county.id,
      county_name: county.name,
    }));
    setCountyListOpen(false);
  }

  function animateViewBox(target) {
    const svg = mapContainerRef.current?.querySelector('svg');
    if (!svg) return;
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    const start = parseViewBox(svg.getAttribute('viewBox')) || currentViewBoxRef.current;
    const startedAt = performance.now();
    const duration = 620;
    mapPanelRef.current?.classList.add('is-zooming');
    const frame = (now) => {
      const raw = Math.min(1, (now - startedAt) / duration);
      const progress = 1 - ((1 - raw) ** 3);
      const next = start.map((valueAtStart, index) => valueAtStart + ((target[index] - valueAtStart) * progress));
      svg.setAttribute('viewBox', next.join(' '));
      currentViewBoxRef.current = next;
      if (raw < 1) {
        animationRef.current = requestAnimationFrame(frame);
      } else {
        mapPanelRef.current?.classList.remove('is-zooming');
      }
    };
    animationRef.current = requestAnimationFrame(frame);
  }

  useEffect(() => {
    if (!open || !svgText) return;
    const svg = mapContainerRef.current?.querySelector('svg');
    if (!svg) return;
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('shape-rendering', 'geometricPrecision');
    svg.setAttribute('viewBox', currentViewBoxRef.current.join(' '));
    const shapes = Array.from(svg.querySelectorAll('[data-city-id]'));
    shapes.forEach((shape) => {
      const sameProvince = shape.dataset.provinceCode === draft.province_code;
      const sameCounty = shape.dataset.cityId === draft.county_id;
      shape.classList.toggle('is-muted', Boolean(draft.province_code) && !sameProvince);
      shape.classList.toggle('is-active-province', Boolean(draft.province_code) && sameProvince);
      shape.classList.toggle('is-selected-county', Boolean(draft.county_id) && sameCounty);
    });
  }, [open, svgText, draft.province_code, draft.county_id]);

  useEffect(() => {
    if (!open || !svgText) return;
    const svg = mapContainerRef.current?.querySelector('svg');
    if (!svg) return;
    const shapes = Array.from(svg.querySelectorAll('[data-city-id]'));

    if (!draft.province_code) {
      animateViewBox(FULL_VIEWBOX);
      return;
    }
    const provinceShapes = shapes.filter((shape) => shape.dataset.provinceCode === draft.province_code);
    if (!provinceShapes.length) return;
    const boxes = provinceShapes.map((shape) => shape.getBBox());
    const minX = Math.min(...boxes.map((box) => box.x));
    const minY = Math.min(...boxes.map((box) => box.y));
    const maxX = Math.max(...boxes.map((box) => box.x + box.width));
    const maxY = Math.max(...boxes.map((box) => box.y + box.height));
    const padding = Math.max(34, Math.max(maxX - minX, maxY - minY) * 0.14);
    animateViewBox([minX - padding, minY - padding, (maxX - minX) + (padding * 2), (maxY - minY) + (padding * 2)]);
  }, [open, svgText, draft.province_code]);

  function handleMapClick(event) {
    const shape = event.target.closest?.('[data-city-id]');
    if (!shape) return;
    const county = counties.find((item) => item.id === shape.dataset.cityId);
    if (!county) return;
    selectCounty(county);
  }

  function handleBackdropWheel(event) {
    if (event.target.closest?.('.iran-address-sidebar, .iran-county-list, .iran-address-line-field textarea')) return;
    event.preventDefault();
  }

  if (!open) return null;

  return (
    <div className="iran-address-backdrop" onMouseDown={onClose} onWheel={handleBackdropWheel}>
      <section className="iran-address-modal" onMouseDown={(event) => event.stopPropagation()} dir="rtl">
        <header className="iran-address-header">
          <div>
            <div className="flex items-center gap-2 text-base font-black text-slate-900">
              <MapPin className="h-5 w-5 text-[#005bbb]" />
              انتخاب استان و شهرستان
            </div>
            <p className="mt-1 text-xs text-slate-500">ابتدا استان و سپس شهرستان محل آدرس را انتخاب کنید.</p>
          </div>
          <button type="button" onClick={onClose} className="iran-address-icon-button" aria-label="انصراف و بستن">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="iran-address-body">
          <aside className="iran-address-sidebar">
            <label className="text-xs font-bold text-slate-600">استان</label>
            <select
              value={draft.province_code}
              onChange={(event) => {
                const province = provinces.find((item) => item.code === event.target.value);
                if (province) selectProvince(province.code, province.name);
                else selectProvince('', '');
              }}
              className="iran-address-select"
            >
              <option value="">انتخاب استان</option>
              {provinces.map((province) => <option key={province.code} value={province.code}>{province.name}</option>)}
            </select>

            {draft.province_code ? (
              <>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <label className="text-xs font-bold text-slate-600">شهرستان</label>
                  <button type="button" onClick={() => selectProvince('', '')} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#005bbb]">
                    <RotateCcw className="h-3.5 w-3.5" /> کل ایران
                  </button>
                </div>
                <div className="iran-address-search">
                  <Search className="h-4 w-4" />
                  <input
                    value={search}
                    onFocus={() => setCountyListOpen(true)}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setCountyListOpen(true);
                    }}
                    placeholder="جستجوی شهرستان..."
                  />
                </div>
                {countyListOpen ? (
                  <div className="iran-county-list">
                    {visibleCounties.map((county) => (
                      <button
                        key={county.id}
                        type="button"
                        onClick={() => selectCounty(county)}
                        className={draft.county_id === county.id ? 'is-selected' : ''}
                      >
                        <span>{county.name}</span>
                        {draft.county_id === county.id ? <Check className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    ))}
                    {!visibleCounties.length && <div className="p-4 text-center text-xs text-slate-500">شهرستانی پیدا نشد.</div>}
                  </div>
                ) : (
                  <button type="button" className="iran-county-selected-summary" onClick={() => setCountyListOpen(true)}>
                    <span>{draft.county_name}</span>
                    <span>تغییر شهرستان</span>
                  </button>
                )}
              </>
            ) : (
              <div className="iran-address-hint">از فهرست یا با کلیک روی نقشه، استان را انتخاب کنید.</div>
            )}

            <div className="iran-address-line-field">
              <label className="text-xs font-bold text-slate-600">نشانی کامل</label>
              <textarea
                rows={4}
                value={draft.address_line}
                onChange={(event) => setDraft((current) => ({ ...current, address_line: event.target.value }))}
                placeholder="خیابان، کوچه، پلاک و واحد"
              />
            </div>
          </aside>

          <div ref={mapPanelRef} className="iran-map-panel">
            {mapError ? (
              <div className="text-sm font-bold text-red-600">فایل نقشه بارگذاری نشد.</div>
            ) : svgText ? (
              <div ref={mapContainerRef} className="iran-map-canvas" onClick={handleMapClick} dangerouslySetInnerHTML={{ __html: svgText }} />
            ) : (
              <div className="text-sm font-bold text-slate-500">در حال بارگذاری نقشه...</div>
            )}
            {draft.province_name && (
              <div className="iran-map-selection-badge">
                {draft.province_name}{draft.county_name ? `، ${draft.county_name}` : ''}
              </div>
            )}
          </div>
        </div>

        <footer className="iran-address-footer">
          <div className="min-w-0 text-xs text-slate-500">
            {draft.county_name ? `انتخاب فعلی: ${draft.province_name}، ${draft.county_name}` : 'برای ثبت، شهرستان و نشانی کامل را وارد کنید.'}
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={onClose} className="iran-address-cancel">انصراف</button>
            <button type="button" disabled={!draft.county_id || !draft.address_line.trim()} onClick={() => onConfirm(draft)} className="iran-address-confirm">تأیید نشانی</button>
          </div>
        </footer>
      </section>
    </div>
  );
}
